export type RefreshTokenResult = {
  accessToken: string;
  refreshToken?: string;
};

export interface RefreshTokenManager {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(input: { accessToken: string; refreshToken?: string }): void;
  clearTokens(): void;
  refreshAccessToken(): Promise<RefreshTokenResult>;
  withAutoRefresh<T>(request: () => Promise<T>): Promise<T>;
  setOnLogout(handler: () => void): void;
}

export interface RefreshTokenManagerConfig {
  refreshAccessToken(refreshToken: string): Promise<RefreshTokenResult>;
  isUnauthorizedError?(error: unknown): boolean;
}

type RefreshInFlight = {
  epoch: number;
  promise: Promise<RefreshTokenResult>;
};

export function createRefreshTokenManager(config: RefreshTokenManagerConfig): RefreshTokenManager {
  let accessToken: string | null = null;
  let refreshToken: string | null = null;
  let onLogout: (() => void) | null = null;

  let sessionEpoch = 0;
  let refreshInFlight: RefreshInFlight | null = null;

  function isUnauthorizedError(error: unknown): boolean {
    if (config.isUnauthorizedError) {
      return config.isUnauthorizedError(error);
    }

    return defaultIsUnauthorizedError(error);
  }

  function getAccessToken(): string | null {
    return accessToken;
  }

  function getRefreshToken(): string | null {
    return refreshToken;
  }

  function setTokens(input: { accessToken: string; refreshToken?: string }): void {
    accessToken = input.accessToken;
    if (typeof input.refreshToken === 'string') {
      refreshToken = input.refreshToken;
    }

    sessionEpoch += 1;
    refreshInFlight = null;
  }

  function clearTokens(): void {
    accessToken = null;
    refreshToken = null;
    sessionEpoch += 1;
    refreshInFlight = null;
  }

  function setOnLogout(handler: () => void): void {
    onLogout = handler;
  }

  function logoutIfCurrentEpoch(epoch: number): void {
    if (sessionEpoch !== epoch) {
      return;
    }

    if (accessToken === null && refreshToken === null) {
      return;
    }

    accessToken = null;
    refreshToken = null;
    sessionEpoch += 1;
    refreshInFlight = null;

    try {
      onLogout?.();
    } catch (logoutHandlerError) {
      void logoutHandlerError;
    }
  }

  function refreshAccessToken(): Promise<RefreshTokenResult> {
    const epochAtStart = sessionEpoch;

    if (refreshInFlight?.epoch === epochAtStart) {
      return refreshInFlight.promise;
    }

    const promise = (async (): Promise<RefreshTokenResult> => {
      const tokenForRefresh = refreshToken;
      if (!tokenForRefresh) {
        throw new Error('No refresh token available for access token refresh');
      }

      const result = await config.refreshAccessToken(tokenForRefresh);
      if (!isRefreshTokenResult(result)) {
        throw new Error('Invalid refresh token response');
      }

      if (sessionEpoch === epochAtStart) {
        accessToken = result.accessToken;
        if (result.refreshToken !== undefined) {
          refreshToken = result.refreshToken;
        }
      }

      return result;
    })()
      .catch((error: unknown) => {
        logoutIfCurrentEpoch(epochAtStart);
        throw error;
      })
      .finally(() => {
        if (refreshInFlight?.epoch === epochAtStart) {
          refreshInFlight = null;
        }
      });

    refreshInFlight = { epoch: epochAtStart, promise };
    return promise;
  }

  async function withAutoRefresh<T>(request: () => Promise<T>): Promise<T> {
    const epochAtStart = sessionEpoch;

    try {
      return await request();
    } catch (error) {
      if (!isUnauthorizedError(error)) {
        throw error;
      }

      if (sessionEpoch !== epochAtStart) {
        throw error;
      }

      try {
        await refreshAccessToken();
      } catch {
        throw error;
      }

      if (sessionEpoch !== epochAtStart) {
        throw error;
      }

      try {
        return await request();
      } catch (retryError) {
        if (isUnauthorizedError(retryError)) {
          logoutIfCurrentEpoch(epochAtStart);
        }
        throw retryError;
      }
    }
  }

  return {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearTokens,
    refreshAccessToken,
    withAutoRefresh,
    setOnLogout,
  };
}

function defaultIsUnauthorizedError(error: unknown): boolean {
  return getErrorStatus(error) === 401;
}

function getErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const record = error as Record<string, unknown>;
  const status = record.status;
  if (typeof status === 'number') {
    return status;
  }

  const statusCode = record.statusCode;
  if (typeof statusCode === 'number') {
    return statusCode;
  }

  const response = record.response;
  if (response && typeof response === 'object') {
    const responseRecord = response as Record<string, unknown>;
    const responseStatus = responseRecord.status;
    if (typeof responseStatus === 'number') {
      return responseStatus;
    }
  }

  return null;
}

function isRefreshTokenResult(value: unknown): value is RefreshTokenResult {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.accessToken !== 'string') {
    return false;
  }

  const refreshToken = record.refreshToken;
  return refreshToken === undefined || typeof refreshToken === 'string';
}
